/** @format */

export interface LinkableItem {
  nid?: string | number;
  id?: string | number;
  link?: string;
  public?: boolean;
}

export const toSameOriginPath = (link?: string | null): string | null | undefined => {
  if (!link || typeof link !== 'string' || typeof window === 'undefined') {
    return link;
  }

  const origin = window.location.origin;
  if (!link.startsWith(origin)) {
    return link;
  }

  return link.replace(origin, '') || '/';
};

const getRedirectionLink = (item?: LinkableItem | null): string => {
  if (!item) return '/404';

  if (item.nid) {
    return `/page/${item.nid}`;
  }

  const link = item.link || '/404';
  return toSameOriginPath(link) || '/404';
};

export const getKnowledgeBaseLink = (kb?: string | null, item?: LinkableItem | null): string => {
  if (!item || !kb) return '/404';

  if (item.nid) {
    return item.public
      ? `/knowledge-base/${kb}/article/public/${item.nid}`
      : `/knowledge-base/${kb}/article/${item.nid}`;
  }

  return item.link || '/404';
};

export const getNewsLink = (item?: LinkableItem | null): string => {
  if (!item) return '/404';

  if (item.nid) {
    return item.public ? `/news/public/${item.nid}` : `/news/${item.nid}`;
  }

  return item.link || '/404';
};

export const getNewsCategoryLabel = (category?: string | { title?: string } | null): string => {
  if (!category) return '';
  if (typeof category === 'string') return category;
  return category.title || '';
};

export const getEventLink = (item?: LinkableItem | null): string => {
  if (!item) return '/events';

  if (item.link) {
    return item.link;
  }

  if (item.nid || item.id) {
    return `/events/${item.nid || item.id}`;
  }

  return '/events';
};

export const getFileBase64 = (file: Blob): Promise<string | null> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64Str = reader.result;
      if (typeof base64Str !== 'string') {
        resolve(null);
        return;
      }
      resolve(base64Str.split(';base64,').length > 1 ? base64Str.split(';base64,')[1] : null);
    };
    reader.onerror = reject;
  });
};

export const formatDate = (
  value?: string | number | Date | null,
  _isAdministrationContext = false,
  locale = 'en'
): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };

  return d.toLocaleDateString(locale, options);
};

const KEPT_SCENARIOS = new Set(['workshop-pv', 'partner', 'aumovio-internal']);

/**
 * Map stored scenario to a display key. Legacy conti-internal → aumovio-internal.
 */
export const resolveScenarioDisplayKey = (scenario?: string | null): string | null => {
  if (!scenario || typeof scenario !== 'string') {
    return null;
  }

  const key = scenario === 'conti-internal' ? 'aumovio-internal' : scenario;
  return KEPT_SCENARIOS.has(key) ? key : null;
};

export const getScenarioLabel = (scenario: string | undefined | null, t: (key: string) => string): string => {
  const key = resolveScenarioDisplayKey(scenario);
  return key ? t(`personalData.scenario.${key}`) : '';
};

/**
 * First role string that starts with an uppercase letter (e.g. "Extranet Admin").
 */
export const getPrimaryCapitalizedRole = (roles?: unknown): string => {
  if (!Array.isArray(roles)) {
    return '';
  }

  const match = roles.find((role): role is string => typeof role === 'string' && /^[A-Z]/.test(role));
  return match ?? '';
};

export default getRedirectionLink;
